import { storyblokApi } from "@/lib/storyblok-api";

import { storyblokCache } from "./storyblok-cache";

export type FaqTopic = { value: string; name: string };

export const FAQ_TOPICS: readonly FaqTopic[] = [
  { value: "accessibility", name: "Accessibility" },
  { value: "aftercare-and-recovery", name: "Aftercare and recovery" },
  { value: "astigmatism", name: "Astigmatism" },
  { value: "cataract-surgery", name: "Cataract surgery" },
  { value: "consultation", name: "Consultation" },
  { value: "eye-conditions", name: "Eye conditions" },
  { value: "hyperopia", name: "Hyperopia" },
  { value: "laser-eye-surgery", name: "Laser eye surgery" },
  { value: "laser-lifetime-care-guarantee", name: "Laser lifetime care guarantee" },
  { value: "lens-replacement-surgery", name: "Lens replacement surgery" },
  { value: "myopia", name: "Myopia" },
  { value: "clinic-questions", name: "Clinic questions" },
  { value: "preparing-for-surgery", name: "Preparing for surgery" },
  { value: "presbyopia", name: "Presbyopia" },
  { value: "pricing-and-payment", name: "Pricing and payment" },
  { value: "results", name: "Results" },
  { value: "suitability", name: "Suitability" },
];

const TOPIC_BY_VALUE = new Map<string, FaqTopic>(FAQ_TOPICS.map((t) => [t.value, t]));

export function findTopic(value: string | undefined): FaqTopic | undefined {
  if (!value) return undefined;
  return TOPIC_BY_VALUE.get(value);
}

export function humanizeTopic(value: string): string {
  return value
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function topicLabel(value: string | undefined): string {
  if (!value) return "";
  return findTopic(value)?.name ?? humanizeTopic(value);
}

export function resolveTopicName(value: string, topics: FaqTopic[]): string {
  return (
    topics.find((t) => t.value === value)?.name ?? findTopic(value)?.name ?? humanizeTopic(value)
  );
}

type DatasourceEntry = { name?: string; value?: string };

export async function fetchFaqTopics(isDraft = false): Promise<FaqTopic[]> {
  try {
    const { data } = await (
      await storyblokApi()
    ).get(
      "cdn/datasource_entries",
      { datasource: "faq_topics", per_page: 1000 },
      storyblokCache(isDraft, ["faqs"]),
    );
    const entries = (data?.datasource_entries ?? []) as DatasourceEntry[];
    const topics = entries
      .filter((e): e is FaqTopic => Boolean(e.value && e.name))
      .map((e) => ({ value: e.value, name: e.name }));
    return topics.length > 0 ? topics : [...FAQ_TOPICS];
  } catch {
    return [...FAQ_TOPICS];
  }
}
